/**
 * Bucketed ranges for headcount, revenue and funding.
 *
 * Each bucket carries three separate things that used to be one English string:
 *
 *  - `id` — the stable identifier. Compared, selected, and safe to persist.
 *  - `storedLabel` — the vendor-canonical English string. It is what the data
 *    provider and the search index actually hold, so it is a wire value: it is
 *    what gets sent and what record values are matched against. It is never the
 *    right thing to show a non-English user, and it must never change.
 *  - `labelKey` + `unit` — how to render the bucket for a human. Money buckets
 *    are locale-formatted ("$1M" / "1 Mio. $"), so the label is composed at
 *    render time rather than stored.
 *
 * This mirrors the backend's own split in `mdm/derived/ranges.py`
 * (`stored_label` vs `display_label`).
 */

export type RangeUnit = "currency" | "count";

/** The i18n key a bucket renders through. */
export type RangeLabelKey = "ranges.under" | "ranges.between" | "ranges.over";

export interface RangeType {
  /** Stable identifier — namespaced per catalog so ids never collide. */
  id: string;
  min: number | null;
  max: number | null;
  /**
   * Vendor-canonical English string. This is a wire value (what the index
   * stores and what the API is sent), NOT a display label.
   */
  storedLabel: string;
  /** Absent for the "no value" placeholder, which renders as a dash. */
  labelKey?: RangeLabelKey;
  unit?: RangeUnit;
}

/** Placeholder row meaning "no value recorded". Rendered as a dash, never translated. */
export const EMPTY_RANGE_STORED_LABEL = "-";

export const RevenueRanges: RangeType[] = [
  { id: "revenue:none", min: 0, max: 0, storedLabel: EMPTY_RANGE_STORED_LABEL },
  { id: "revenue:under-1m", min: 0, max: 1_000_000, storedLabel: "Under $1M", labelKey: "ranges.under", unit: "currency" },
  { id: "revenue:1m-10m", min: 1_000_000, max: 10_000_000, storedLabel: "$1M to $10M", labelKey: "ranges.between", unit: "currency" },
  { id: "revenue:10m-50m", min: 10_000_000, max: 50_000_000, storedLabel: "$10M to $50M", labelKey: "ranges.between", unit: "currency" },
  { id: "revenue:50m-100m", min: 50_000_000, max: 100_000_000, storedLabel: "$50M to $100M", labelKey: "ranges.between", unit: "currency" },
  { id: "revenue:100m-1b", min: 100_000_000, max: 1_000_000_000, storedLabel: "$100M to $1B", labelKey: "ranges.between", unit: "currency" },
  { id: "revenue:over-1b", min: 1_000_000_000, max: null, storedLabel: "$1B and Over", labelKey: "ranges.over", unit: "currency" },
];

export const EmployeeRanges: RangeType[] = [
  { id: "employees:none", min: 0, max: 0, storedLabel: EMPTY_RANGE_STORED_LABEL },
  { id: "employees:1-10", min: 1, max: 10, storedLabel: "1 to 10", labelKey: "ranges.between", unit: "count" },
  { id: "employees:11-50", min: 11, max: 50, storedLabel: "11 to 50", labelKey: "ranges.between", unit: "count" },
  { id: "employees:51-200", min: 51, max: 200, storedLabel: "51 to 200", labelKey: "ranges.between", unit: "count" },
  { id: "employees:201-500", min: 201, max: 500, storedLabel: "201 to 500", labelKey: "ranges.between", unit: "count" },
  { id: "employees:501-1000", min: 501, max: 1000, storedLabel: "501 to 1000", labelKey: "ranges.between", unit: "count" },
  { id: "employees:1k-5k", min: 1001, max: 5000, storedLabel: "1K to 5K", labelKey: "ranges.between", unit: "count" },
  { id: "employees:5k-10k", min: 5001, max: 10000, storedLabel: "5K to 10K", labelKey: "ranges.between", unit: "count" },
  { id: "employees:over-10k", min: 10001, max: null, storedLabel: "10K+", labelKey: "ranges.over", unit: "count" },
];

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Format one bound of a bucket.
 *
 * The buckets are defined in USD — they come from the enrichment vendor, not
 * from the workspace's currency — so the currency is pinned while the grouping,
 * decimal marks and symbol placement follow the reader's locale.
 */
export function formatRangeAmount(value: number, unit: RangeUnit, locale?: string): string {
  const resolved = locale || (typeof navigator !== "undefined" ? navigator.language : "en") || "en";
  try {
    return new Intl.NumberFormat(resolved, {
      notation: "compact",
      maximumFractionDigits: 0,
      ...(unit === "currency" ? { style: "currency" as const, currency: "USD" } : {}),
    }).format(value);
  } catch {
    return String(value);
  }
}

/**
 * The text a user reads for a bucket.
 *
 * Never feed the result back into a filter or a payload — that is what `id` and
 * `storedLabel` are for.
 */
export function displayRangeLabel(
  range: RangeType,
  t: Translate,
  options?: { locale?: string },
): string {
  if (!range.labelKey) return EMPTY_RANGE_STORED_LABEL;
  const unit = range.unit ?? "count";
  const amount = (value: number) => formatRangeAmount(value, unit, options?.locale);
  switch (range.labelKey) {
    case "ranges.under":
      return t("ranges.under", { amount: amount(range.max ?? 0) });
    case "ranges.over":
      return t("ranges.over", { amount: amount(range.min ?? 0) });
    case "ranges.between":
      return t("ranges.between", { min: amount(range.min ?? 0), max: amount(range.max ?? 0) });
    default:
      return range.storedLabel;
  }
}

/** Look a bucket up by its id, or by its vendor-canonical stored label. */
export function findRangeById(ranges: RangeType[], idOrStoredLabel: string): RangeType | undefined {
  return (
    ranges.find(r => r.id === idOrStoredLabel) ??
    ranges.find(r => r.storedLabel === idOrStoredLabel)
  );
}

/**
 * Get the range object that matches the given min/max values
 */
export const getRange = (
  ranges: RangeType[],
  min: number | null | undefined,
  max: number | null | undefined
): RangeType | undefined => {
  return ranges.find(
    (range) =>
      (min === range.min || min === null) && (max === range.max || max === null)
  );
};

/**
 * Find the bucket a min/max pair falls into.
 *
 * Exact boundary match first (backend data stores the bucket bounds), then a
 * value-in-range lookup on the max.
 */
export function findMatchingRangeBucket(
  minInput: number | null | undefined,
  maxInput: number | null | undefined,
  ranges: RangeType[],
): RangeType | undefined {
  const exactMatch = ranges.find(
    (r) =>
      (r.min === minInput || (r.min === null && minInput == null)) &&
      (r.max === maxInput || (r.max === null && maxInput == null))
  );
  if (exactMatch && exactMatch.labelKey) return exactMatch;

  const maxVal = maxInput ?? minInput ?? 0;

  for (const range of ranges) {
    const rangeMax = range.max ?? Infinity;
    const rangeMin = range.min ?? 0;

    if (maxVal >= rangeMin && maxVal < rangeMax) {
      return range.labelKey ? range : undefined;
    }

    // Edge case: value equals rangeMin and max is null (e.g. $1B and Over)
    if (range.max === null && maxVal >= rangeMin) {
      return range.labelKey ? range : undefined;
    }
  }

  return undefined;
}

/**
 * The stored (vendor-canonical) label for a min/max pair.
 *
 * Still returns the English string on purpose: callers use it as a record value
 * or a select value, not as something to show. Use `findMatchingRangeBucket`
 * plus `displayRangeLabel` when the result is rendered.
 */
export const findMatchingRange = (
  minInput: number | null | undefined,
  maxInput: number | null | undefined,
  ranges: RangeType[]
): string => findMatchingRangeBucket(minInput, maxInput, ranges)?.storedLabel ?? "";
